import React, { useEffect, useState, useMemo } from 'react'
import { Flex, Text } from '@chakra-ui/core'
import { useFormContext, Controller } from 'react-hook-form'
import { DayCircle } from './DayCircle'
import useLocaleText from 'components/useLocaleText'
import { DAYS_OF_WEEK_ORDERED } from './utils'
import dayjs from 'dayjs'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { FrequencyType } from 'components/NewsFeed/PollTaskForm/utils'

export const WeeklySelector = () => {
  const { watch, setValue, errors, clearError, control, triggerValidation } = useFormContext()
  const { setMaxAllowedOverdueDays } = useFormStepContext()
  const t_choose_range = useLocaleText('t_choose_range')

  // Retrieve the current state of the form
  const daysOfWeek = watch('days_of_week') ?? []
  const fromTs = dayjs(watch('from_ts'))

  // Calculate the starting day index from `from_ts`
  const startDayIndex = useMemo(() => {
    return fromTs ? fromTs.day() : dayjs().day()
  }, [fromTs])

  // Local state for range selection
  const [range, setRange] = useState([startDayIndex, daysOfWeek.length > 1 ? daysOfWeek[1] : null])
  const [initialized, setInitialized] = useState(false)

  // Function to calculate max allowed overdue days based on selected range
  const calculateMaxAllowedOverdueDays = (selectedDays: number[]): number => {
    if (!selectedDays || selectedDays.length === 0) return 8

    // If all days of week are selected (7 days), return 2
    if (selectedDays.length === 7) {
      return 2
    }

    // Calculate number of days in the range (including boundary days)
    const startDay = selectedDays[0]
    const endDay = selectedDays[1]

    let daysInRange: number

    if (startDay <= endDay) {
      // Normal range: [3, 5] -> 3 days (3, 4, 5)
      daysInRange = endDay - startDay + 1

      // Special case: if startDay and endDay are the same (like [0, 0], [1, 1])
      // it means every day, so return 7
      if (startDay === endDay) {
        daysInRange = 7
      }
    } else {
      // Cyclic range: [6, 0] -> 6 days (0, 1, 2, 3, 4, 5), [6, 3] -> 5 days (0, 1, 2, 3, 4)
      // For [startDay, endDay], count days from 0 to endDay + days from startDay to 6
      daysInRange = endDay + 1 + (6 - startDay + 1)

      // But if this exceeds 7, it means we're counting the same days twice
      // For example: [0, 0] should be 7 days, not 14
      if (daysInRange > 7) {
        daysInRange = 7
      }
    }

    // Calculate: period (7) - number of days in range
    let maxDays: number

    if (daysInRange === 7) {
      // Special case: if all days are selected, maxDays should be 2 (from 0 to 1)
      maxDays = 2
    } else {
      // Otherwise: period (7) - number of days in range + 1 (for dropdown option 0)
      maxDays = 7 - daysInRange + 1
    }

    return maxDays
  }

  // Handle day click for range selection
  const handleDayClick = async (dayIndex: number) => {
    if (range[0] === null || range[1] !== null) {
      // If the start day is not set or the range is complete, start a new selection
      setRange([dayIndex, null])
      setValue('days_of_week', [dayIndex, null])
    } else {
      // If the start day is not set or the range is complete, start a new selection
      setRange([range[0], dayIndex])
      setValue('days_of_week', [range[0], dayIndex])

      // Calculate and set allowed_overdue_days when range is complete
      const selectedDays = [range[0], dayIndex]
      const maxDays = calculateMaxAllowedOverdueDays(selectedDays)

      setValue('allowed_overdue_days', '')
      setMaxAllowedOverdueDays(maxDays)
    }

    // If the start day is not set or the range is complete, start a new selection
    await triggerValidation()
    if (errors?.days_of_week) {
      clearError('days_of_week')
    }
  }

  // Check if a day is within the selected range
  const isDayInRange = (dayIndex: number): boolean => {
    if (range[0] === null || range[1] === null) return false

    // Check for a normal range (start < end)
    if (range[0] < range[1]) {
      return dayIndex > range[0] && dayIndex < range[1]
    }

    // Check for a cyclic range (start > end)
    return dayIndex > range[0] || dayIndex < range[1]
  }

  // Check if a day is a boundary day (start or end of the range)
  const isBoundaryDay = (dayIndex: number): boolean => {
    return dayIndex === range[0] || dayIndex === range[1]
  }

  // Check if a day is a boundary day (start or end of the range)
  useEffect(() => {
    if (!initialized && daysOfWeek.length > 0) {
      setRange([daysOfWeek[0], daysOfWeek[1] ?? null])
      setInitialized(true) // Mark initialization as complete

      // If we have a complete range, calculate and set allowed_overdue_days
      if (daysOfWeek.length === 2 && daysOfWeek[1] !== null) {
        const maxDays = calculateMaxAllowedOverdueDays(daysOfWeek)
        setValue('allowed_overdue_days', '')
        setMaxAllowedOverdueDays(maxDays)
      }
    }
  }, [daysOfWeek, initialized, setValue, setMaxAllowedOverdueDays])

  return (
    <>
      <Text fontSize="12px">{t_choose_range}</Text>
      <Controller
        control={control}
        name="days_of_week"
        rules={{
          validate: (value) => {
            if (!Array.isArray(value) || value.length === 0 || value.some((day) => day === null)) {
              return 'Please select range'
            }
            return true
          },
        }}
        as={
          <Flex justifyContent="space-between" position="relative" my="30px">
            {DAYS_OF_WEEK_ORDERED.map(({ index, label }) => (
              <DayCircle
                key={index}
                day={label}
                isSelected={isBoundaryDay(index)}
                isInRange={isDayInRange(index)}
                onClick={() => handleDayClick(index)}
              />
            ))}
          </Flex>
        }
      />
    </>
  )
}
